/**
 * Authors: Koen, mariushirjanu, Triadi
 * Created: 26/10/2018
 * Upgraded: 28/10/2022
 */
import * as _ from 'underscore';
import { Component, Input, OnInit, OnChanges, OnDestroy, AfterViewInit, SimpleChanges, ViewChild, ViewContainerRef, ElementRef, Inject } from '@angular/core';
import { GaEventService, IGAEvent } from '../services/ga-event.service';
import { Subscription } from 'rxjs';
import { GaWizard, IWizardStep, WizardEvent } from './ga-wizard';
import { GaWizardService } from './ga-wizard.service';
import { IExtendedWindow } from '../../../type_definitions/datatypes';
import { cssUtil } from '../util';

@Component({
  selector: 'wizard-controls',
  template: `
    <div class="wizard-controls">
      <button class="btn btn-default prev" (click)="selectStep(activeStepIndex - 1)" [disabled]="!steps[activeStepIndex - 1] || navigationDisabled">
        <i class="fa fa-caret-left"></i><span class="button-text d-none d-sm-inline" translate>APP.WIZARD.PREVIOUS</span>
      </button>
      <div class="btn-group dropdown" dropdown #dropdown="bs-dropdown" (isOpenChange)="recalculateDropdownHeight()">
        <button class="btn btn-default menu dropdown-toggle" [ngClass]="{'step-complete': isStepCompleted(activeStepId())}" type="button" dropdownToggle [disabled]="navigationDisabled">
          <span class="dropdown-title d-none d-sm-inline">{{ steps[ activeStepIndex ]?.title | translate }}</span>
        </button>
        <ul *dropdownMenu class="dropdown-menu wizard-controls-menu" role="menu">
          <li *ngFor="let step of steps; let idx = index; trackById" [ngClass]="{'active': idx == activeStepIndex, 'step-complete': isStepCompleted(step.id), 'step-incomplete': !isStepCompleted(step.id)}">
            <a href="javascript:void(0)" role="menuitem" (click)="selectStep(idx)" translate>{{ step.title }}</a>
          </li>
        </ul>
      </div>
      <button *ngIf="!(finalStep && extraStep?.children?.length)" class="btn btn-default next" (click)="selectStep(activeStepIndex + 1)" [disabled]="!steps[activeStepIndex + 1] || navigationDisabled">
        <i class="fa fa-caret-right"></i><span class="button-text d-none d-sm-inline" translate>APP.WIZARD.NEXT</span>
      </button>
      <span #extraStep [hidden]="!(finalStep && extraStep?.children?.length)" class="final hidden-xxs">
        <ng-content></ng-content>
      </span>
    </div>
    <div style="display:none">
      <ng-template #shadowValidationTemplate></ng-template>
    </div>
  `
})
export class WizardControlsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() public wizardId:string;
  @Input() public completedSteps?:string[];
  @Input() public navigationDisabled?:boolean;
  @ViewChild('shadowValidationTemplate', { read: ViewContainerRef }) public shadowValidationTemplate:ViewContainerRef;

  public steps:IWizardStep[];
  public finalStep:boolean = false;
  private wizard:GaWizard;
  private eventHandler:Subscription;
  private _activeStepIndex:number;

  constructor(
    private gaEventService:GaEventService,
    private wizardService:GaWizardService,
    private elementRef:ElementRef<HTMLElement>,
    @Inject(Window) private window:IExtendedWindow
  ) {
    /***/
  }

  public ngOnInit():void {
    // if wizard id is provided then isolate instance of wizard services
    this.wizard = this.wizardService.getInstance(this.wizardId);

    this.steps = this.wizard.visibleSteps();
    this.activeStepIndex = this.wizard.activeStepIndex;
    this.listen();
  }

  public ngAfterViewInit():void {
    this.wizard.shadowValidationTemplate = this.shadowValidationTemplate;
  }

  private listen = () => {
    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      const refreshSteps = ():void => {
        this.steps = this.wizard.visibleSteps();
        this.finalStep = this.wizard.activeStepIndex === (this.steps.length - 1);
      };

      if (event.name === `${WizardEvent.STEP_SELECTION_CHANGED}.${this.wizardId}`) {
        this.activeStepIndex = event.data;
        refreshSteps();
      } else if (event.name === `${WizardEvent.SYNC}.${this.wizardId}`) {
        refreshSteps();
      }
    });
  };

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.navigationDisabled) {
      this.navigationDisabled = changes.navigationDisabled.currentValue;
    }

    if (changes.completedSteps) {
      this.completedSteps = _.clone(changes.completedSteps.currentValue);
    }
  }

  public get activeStepIndex():number {
    return this._activeStepIndex;
  }
  public set activeStepIndex(activeStepIndex:number) {
    this._activeStepIndex = activeStepIndex;
  }

  public activeStepId():string {
    return this.wizard.activeStepId();
  }

  public selectStep(idx):void {
    this.wizard.selectStep(Number(idx));
  }

  public isStepCompleted(stepId:string):boolean {
    return !!_.find(this.completedSteps, (completedStep:string) => completedStep === stepId);
  }

  public recalculateDropdownHeight():void {
    // recalculate dropdown max-height to always visible on the screen and scrollable
    // add timeout here to let dropdown render fully in DOM
    setTimeout(() => {
      const windowHeight = this.window.frameElement? (this.window.innerHeight || 550) : this.window.innerHeight;
      $(this.elementRef.nativeElement).find('.wizard-controls-menu')
        .css({
          maxHeight: windowHeight - cssUtil.sumHeights('.ui-layout-north;.ui-layout-resizer-north;.fixed-to-top-container')
        });
    });
  }

  public ngOnDestroy(): void {
    this.eventHandler?.unsubscribe();
  }
}